import axios from "axios";
import { atom } from "jotai";
import { SetStateAction } from "jotai/core/types";
import { atomFamily } from "jotai/utils";

import { useGaSweepstakesEventTracker } from "../shared/eventsTracker/ga-tracker/ga-events-tracker";
import { GaSweepstakesEventActions } from "../shared/eventsTracker/ga-tracker/ga-tracker.types";
import { getTotalSweepEntries } from "../shared/utils";

import { EXTRA_ENTRIES_PER_SMS_ACCEPTANCE } from "./widget.constants";
import {
  OptionType,
  QuestionType,
  QuizType,
  QuizVariantEnum,
  SmsDataType,
} from "./widget.types";

export const quizAtom = atom<QuizType, SetStateAction<QuizType>>(
  async () => {
    try {
      const gaSweepstakesEventTracker = useGaSweepstakesEventTracker();

      //Box of samples control for increase extra entries
      const boxSamplesCodeParam = new URL(
        document.location.href
      ).searchParams.get("order");
      const arcadeProductDataStr = sessionStorage.getItem("arcadeProductData");
      const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
      let boxSamplesExtraEntries = 0;
      if (boxSamplesCodeParam && arcadeProductDataStr && submission) {
        window.theme.Utils.getHash(
          `${submission.modified}|${arcadeProductDataStr}`
        )
          .then((hash: string) => {
            if (boxSamplesCodeParam === hash) {
              const SWEEPSTAKE_SESSION_ENTRIES = "sweepstakeSessionEntries";
              const sweepstakeSessionEntries = Number(
                sessionStorage.getItem(SWEEPSTAKE_SESSION_ENTRIES)
              );
              boxSamplesExtraEntries =
                Number(JSON.parse(arcadeProductDataStr).arcadeVariantPoints) ||
                0;
              sessionStorage.setItem(
                SWEEPSTAKE_SESSION_ENTRIES,
                (sweepstakeSessionEntries + boxSamplesExtraEntries).toString()
              );
            } else {
              alert("Invalid order to increase points");
            }
          })
          .catch((error) => console.log(error));
      }
      window.history.replaceState(
        {},
        document.title,
        document.location.href.split("?")[0]
      );

      const userEmail = submission?.data?.email;
      const { data }: { data: QuizType } = await axios.get(
        `${window.FKL.yentaServicesApiUrl}/quiz/${window.FKL.quizSlug.trim()}/${
          userEmail ? encodeURIComponent(userEmail) : "no-email"
        }`
      );

      data.isArcade = data.variant.toUpperCase() === QuizVariantEnum.Arcade;
      if (submission) {
        data.submissionCount = Number(submission.data.submissionCount);
        data.prevSmsExtraEntries = Number(submission.data.smsExtraEntries ?? 0);
        data.prevSmsSelectedCompanies =
          submission.data.smsSelectedCompanies ?? "";

        if (data.isArcade) {
          const lastQuestionAnsweredIndex = data.questions.findIndex(
            (question) => question.id === submission.data.lastQuestionAnsweredId
          );

          if (
            lastQuestionAnsweredIndex >= 0 &&
            lastQuestionAnsweredIndex < data.questions.length - 1
          ) {
            data.questions = data.questions.slice(
              lastQuestionAnsweredIndex + 1,
              data.questions.length
            );
          }

          data.canPlay = true;
          data.allTimePoints =
            Number(submission.data.quizExtraEntries) +
            Number(boxSamplesExtraEntries);
        } else {
          try {
            await window.theme.Sweepstake.prototype.checkHash();
            data.canPlay = true;
          } catch (err) {
            if (
              err === window.theme.Sweepstake.prototype.hashChecks.NO_SUBMISSION
            ) {
              data.canPlay = true;
            }

            data.canPlay = false;
          }

          data.quizCompletionsCount = Number(
            submission.data.quizCompletionsCount ?? 0
          );
          data.prevExtraEntries = Number(submission.data.quizExtraEntries);
          data.allTimePoints = getTotalSweepEntries(submission.data, [
            "smsExtraEntries",
          ]);
          if (data.canPlay) {
            gaSweepstakesEventTracker(GaSweepstakesEventActions.ENTER_FLOW);
          }
        }
      } else {
        data.canPlay = true;
        data.allTimePoints = 0;
        data.submissionCount = 1;
        data.quizCompletionsCount = 0;
        data.prevSmsExtraEntries = 0;
      }

      const prevSmsCompanies = ((submission?.data.smsSelectedCompanies ||
        "") as string).split(",");
      const emailReceiver = (submission?.data.email || "") as string;

      return {
        ...data,
        questions: data.questions.map((question) => ({
          ...question,
          cpl: {
            ...question.cpl,
            emailReceiver,
            registeredCpl: false,
            isAccepted: null,
          },
          options: question.options
            .map<[number, OptionType]>((a) => [Math.random(), a])
            .sort((a, b) => a[0] - b[0])
            .map((a) => a[1]),
          smsInfo: {
            ...question.smsInfo,
            isAccepted: null,
            isTacAccepted: false,
            wasPreviouslyAccepted: prevSmsCompanies.includes(
              question.smsInfo?.brandName ?? ""
            ),
          },
        })),
      } as QuizType;
    } catch (error) {
      throw new Error(error.response?.data?.message ?? error.message);
    }
  },
  (get, set, update) => {
    const newValue =
      typeof update === "function" ? update(get(quizAtom)) : update;
    set(quizAtom, newValue);
  }
);

export const currentPageAtom = atom<number>(0);

export const paginatedQuestionsAtom = atom<
  (QuestionType & { index: number })[][]
>((get) => {
  const result: (QuestionType & { index: number })[][] = [];
  let currentPage = 0;
  const questionsPerPage = window.FKL.questionsPerPage;
  const questions = get(quizAtom)?.questions ?? [];

  for (const [index, question] of questions.entries()) {
    const target = result[currentPage] || (result[currentPage] = []);
    target.push({ ...question, index });
    if (target.length === questionsPerPage) currentPage++;
  }

  return result;
});

export const currentQuestionsAtom = atom(
  (get) => get(paginatedQuestionsAtom)[get(currentPageAtom)]
);

export const currentQuestionIndexAtom = atom((get) => {
  const questions = get(quizAtom)?.questions ?? [];

  const isReminderAnswerMandatory: boolean =
    get(quizAtom).variant.toUpperCase() === QuizVariantEnum.Quiz &&
    window.FKL.allowOptInReminder &&
    (get(quizAtom).submissionCount ?? 1) > 1 &&
    get(currentPageAtom) === 0;

  const index = questions.findIndex((question, i) => {
    let isFinished = true;

    isFinished =
      isFinished && question.options.some((question) => question.isSelected);
    isFinished =
      isFinished && (question.cpl?.isAccepted !== null || !question.cpl?.title);

    if (question.cpl?.formPath && question.cpl?.isAccepted === true) {
      isFinished = isFinished && question.cpl.isTacAccepted;
    }

    if (
      question.smsInfo?.brandName &&
      question.smsInfo.question &&
      question.smsInfo.smsBrandPrivacyPolicy &&
      question.smsInfo.tacSmsBrand
    ) {
      if (
        question.smsInfo.isAccepted === null ||
        (question.smsInfo.isAccepted && !question.smsInfo.isTacAccepted)
      ) {
        isFinished = false;
      }
    }

    if (isReminderAnswerMandatory && i === 0) {
      isFinished = get(remindMeAtom) !== null;
    }

    return !isFinished;
  });

  return index === -1 ? questions.length : index;
});

export const currentQuestionErrorsAtom = atom((get) => {
  const question = (get(quizAtom).questions ?? [])[
    get(currentQuestionIndexAtom)
  ];
  if (!question) return [];

  const errors: (
    | "ANSWER_NOT_SELECTED"
    | "CPL_NOT_SELECTED"
    | "TAC_NOT_ACCEPTED"
  )[] = [];

  if (!question.options.some((option) => option.isSelected)) {
    errors.push("ANSWER_NOT_SELECTED");
  }

  if (question.cpl?.isAccepted === null) {
    errors.push("CPL_NOT_SELECTED");
  }

  if (question.cpl?.isAccepted && !question.cpl.isTacAccepted) {
    errors.push("TAC_NOT_ACCEPTED");
  }

  return errors;
});

export const questionAtom = atomFamily<
  string,
  QuestionType,
  SetStateAction<QuestionType>
>((id) =>
  atom(
    (get) => {
      const questions = get(quizAtom)?.questions ?? [];
      const question = questions.find((question) => question.id === id);

      if (!question) {
        throw new Error(`Question id: ${id} does not exist`);
      }

      return question;
    },
    (_get, set, update) => {
      set(quizAtom, (quiz) => ({
        ...quiz,
        questions: quiz.questions.map((question) =>
          question.id !== id
            ? question
            : typeof update === "function"
            ? update(question)
            : update
        ),
      }));
    }
  )
);

export const questionWithBannerIndexAtom = atom<number | null>(null);

export const remindMeAtom = atom<boolean | null>(null);

export const instructionsModalAtom = atom<boolean>(false);

export const smsAtom = atom<SmsDataType, SetStateAction<SmsDataType>>(
  (get) => {
    const quiz = get(quizAtom);
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

    const selectedCompanies: string[] = Array.from(
      new Set(
        quiz.questions
          .filter((q) => q.smsInfo?.isAccepted && q.smsInfo.isTacAccepted)
          .map((q) => q.smsInfo?.brandName ?? "")
          .filter(Boolean)
      )
    );

    return {
      selectedCompanies,
      extraEntries: selectedCompanies.length * EXTRA_ENTRIES_PER_SMS_ACCEPTANCE,
      phoneNumber: submission?.data.smsMobilePhone ?? "",
    };
  },
  (get, set, update) => {
    const newValue =
      typeof update === "function" ? update(get(smsAtom)) : update;
    set(smsAtom, newValue);
  }
);
